import { TickerSpotV5 } from "bybit-api";
import { DBPosition } from "../api/types";
import { TickerLookup } from "../context/globalDataContext";

export const percent = (amount: number | undefined, decimals = 1): string => {
  if (amount === undefined || amount === null) {
    return "[undefined]%";
  }
  return round(amount, decimals) + "%";
};

export const percentX100 = (
  amount: number | undefined,
  decimals = 1,
): string => {
  if (amount === undefined || amount === null) {
    return "[undefined]%";
  }
  return round(amount * 100, decimals) + "%";
};

export const MINUTE_IN_MS = 1000 * 60;
export const HOUR_IN_MS = MINUTE_IN_MS * 60;
export const DAY_IN_MS = HOUR_IN_MS * 24;

export function shortenWallet(wallet: string | undefined): string {
  if (!wallet) return "[undefined]";
  return wallet.slice(0, 6) + "..." + wallet.slice(-6);
}

export function percentageGrowth(
  startVal: number | undefined,
  endVal: number | undefined,
) {
  if (startVal === undefined || endVal === undefined) {
    return undefined;
  }
  return (endVal - startVal) / startVal;
}

export const euro = (amount: number | undefined, roundDown = false): string => {
  if (amount === undefined || amount === null) {
    return "€[undefined]";
  }
  if (Math.abs(amount) > 100) {
    amount = Math.round(amount);
  }
  if (Math.round(amount) === amount) return "€" + amount;
  if (roundDown) amount = Math.floor(amount * 100) / 100;
  return "€" + amount.toFixed(2);
};

export const dollar = (
  amount: number | undefined,
  roundDown = false,
): string => {
  if (amount === undefined || amount === null) {
    return "$[undefined]";
  }
  if (Math.abs(amount) < 0.01) {
    return "$0";
  }
  if (Math.abs(amount) < 0.1) {
    return "$" + amount.toFixed(4);
  }
  if (Math.abs(amount) < 1) {
    return "$" + amount.toFixed(3);
  }
  if (Math.abs(amount) > 1000) {
    amount = Math.round(amount);
  }

  if (Math.round(amount) === amount) return "$" + amount;

  if (roundDown) amount = Math.floor(amount * 100) / 100;

  return "$" + amount.toFixed(2);
};

export function smartRound(num: number, minDecimal = 4) {
  let start = 1000;
  let startDecimal = minDecimal - 4;

  if (num === 0) {
    return 0;
  }

  while (Math.abs(num) < start) {
    startDecimal++;
    start = start / 10;
  }

  return round(num, Math.max(0, startDecimal));
}

export function round(num: number, decimals: number): number;
export function round(num: undefined, decimals: number): undefined;
export function round(num: number | undefined, decimals: number) {
  if (!num) return num;
  const multiplier = Math.pow(10, decimals);
  return Math.round(num * multiplier) / multiplier;
}

export function tsToStr(timestamp: number) {
  const date = new Date(timestamp);
  return formatDate(date, "m/d h:i");
}

export function formatDate(date: Date, format: string) {
  return format
    .replace("y", date.getFullYear().toString())
    .replace("m", leadingZero(date.getMonth() + 1))
    .replace("d", leadingZero(date.getDate()))
    .replace("h", leadingZero(date.getHours()))
    .replace("i", leadingZero(date.getMinutes()))
    .replace("s", leadingZero(date.getSeconds()));
}

function leadingZero(number: number): string {
  if (number < 10) return "0" + number;
  return number.toString();
}

const compactDollarFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** $21900 -> $21.9K, $1250000 -> $1.3M */
export const compactDollar = (amount: number): string =>
  compactDollarFormat.format(amount);

export function multiplyOrZero(numA?: number, numB?: number): number {
  if (numA === undefined || numB === undefined) {
    return 0;
  }
  return numA * numB;
}

export function multiplyOrUndefined(
  numA?: number,
  numB?: number,
): number | undefined {
  if (numA === undefined || numB === undefined) {
    return undefined;
  }
  return numA * numB;
}

export function getPositionPnL(
  position: DBPosition,
  includeFees = true,
): number | undefined {
  const openPrice = position.openTransaction.price;
  const closedPrice = position.closeTransaction?.price;
  const openFeePercentage = position.openTransaction.feePercent ?? 0;
  const closeFeePercentage = position.closeTransaction?.feePercent ?? 0;

  if (!openPrice || !closedPrice) {
    return undefined;
  }

  let pnL = (closedPrice - openPrice) / openPrice;

  if (includeFees) {
    if (position.type === "SHORT") {
      pnL += openFeePercentage + closeFeePercentage;
    } else {
      pnL -= openFeePercentage + closeFeePercentage;
    }
  }

  if (position.type === "SHORT") {
    return -pnL;
  }

  return pnL;
}

export function getPositionPnLDollar(
  position: DBPosition,
  includeFees = true,
): number | undefined {
  let positionPnL = getPositionPnL(position, includeFees);
  if (positionPnL && positionPnL > 0) {
    positionPnL *= 1.01;
  }
  if (positionPnL && positionPnL < 0) {
    positionPnL *= 0.99;
  }
  const openAmountUSD = multiplyOrUndefined(
    position.price,
    position.coinAmount,
  );

  const dollarPnL = multiplyOrUndefined(positionPnL, openAmountUSD!);

  return dollarPnL;
}

export const getArrFromPair = (pair: string): [string, string] => {
  const quoteRegex = /(EUR|USD|USDT|USDC|USC|BTC)$/gi;
  if (!quoteRegex.test(pair)) {
    // throw new Error(`Pair ${pair} does not end with a valid quote currency.`);
    return [pair, ""];
  }
  const quote = pair.match(quoteRegex)?.[0];
  if (!quote) {
    throw new Error(`Pair ${pair} does not end with a valid quote currency.`);
  }
  const base = pair.replace(quote, "");
  return [base, quote];
};

export const createTickerLookup = (tickers: TickerSpotV5[] | undefined) => {
  const lookup: TickerLookup = {};

  for (const ticker of tickers || []) {
    lookup[ticker.symbol] = parseFloat(ticker.ask1Price);
  }

  return lookup;
};

export const calculateHoldingsPerCoin = (
  positions: DBPosition[],
  tickerLookup: TickerLookup,
) => {
  const holdingPerCoin: { [key: string]: number } = {};

  positions.forEach((position) => {
    if (position.status === "OPEN") {
      const coinWorth = tickerLookup[position.symbol];
      const coinAmount = position.coinAmount;

      if (!coinWorth || !coinAmount) {
        console.warn(
          "Warning, cannot calculate holding for position, coinWorth || coinAmount is undefined. Posotion: ",
          position,
        );
        return;
      }

      if (!holdingPerCoin[position.symbol]) {
        holdingPerCoin[position.symbol] = 0;
      }
      holdingPerCoin[position.symbol] += coinAmount * coinWorth;
    }
  });

  return holdingPerCoin;
};

export const timeAgoToStr = (time: number): string => {
  if (time < 60_000) {
    return "now";
  }

  // makes it minutes
  time = time / 60_000;

  if (time < 60) {
    return "<1 hr ago";
  }

  // makes it hours
  time = time / 60;

  if (time < 24) {
    const rnd = Math.round(time);
    return `${rnd} hr ago`;
  }

  // makes it days
  time = time / 24;

  if (time < 365) {
    const rnd = Math.round(time);
    return `${rnd} days ago`;
  }

  // makes it years
  time = time / 365;
  if (time < 10) {
    const rnd = Math.round(time * 10) / 10;
    return `${time} years ago`;
  }

  return "never";
};

export const differenceBetweenDates = (start: Date, end: Date): string => {
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end < start
  ) {
    return "";
  }

  let diffMs = end.getTime() - start.getTime();
  const msPerSecond = 1000;
  const msPerMinute = msPerSecond * 60;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;

  const days = Math.floor(diffMs / msPerDay);
  diffMs %= msPerDay;
  const hours = Math.floor(diffMs / msPerHour);
  diffMs %= msPerHour;
  const minutes = Math.floor(diffMs / msPerMinute);
  diffMs %= msPerMinute;
  const seconds = Math.floor(diffMs / msPerSecond);

  if (days >= 1) {
    return `${days}d ${String(hours).padStart(2, "0")}h`;
  }

  if (hours >= 1) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }

  if (minutes >= 1) {
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }

  return `${seconds}s`;
};
